import React from 'react'
import managementService from '../../services/managementService'
import ManagementReportShell from './ManagementReportShell'
import useManagementReport from './useManagementReport'
import { DataTable, DrillLinks, KpiGrid, KpiTile, Section, Unavailable } from './ManagementWidgets'
import { at, exportToSpreadsheet, flattenMetrics, isMasked } from './managementUtils'

const LINKS = [
  { label: 'Founder Control', to: '/accounts/reports/founder-control' },
  { label: 'Burn Discipline', to: '/accounts/reports/burn-discipline' },
  { label: 'Runway Sensitivity', to: '/accounts/reports/runway-sensitivity' },
  { label: 'Cash accounts', to: '/accounts/cash-accounts' },
  { label: 'Invoice ageing', to: '/accounts/reports/invoice-aging' }
]

const FinancialHealthReport = () => {
  const report = useManagementReport(managementService.getFinancialHealth)

  const onExport = (data) => exportToSpreadsheet('financial-health', {
    Summary: flattenMetrics(data),
    'Cash accounts': Array.isArray(data.cash?.accounts) ? data.cash.accounts : []
  })

  return (
    <ManagementReportShell
      title="Financial Health"
      subtitle="Cash position, burn, runway, revenue, receivables and budget discipline"
      report={report}
      onExport={onExport}
    >
      {(d) => {
        const m = (path) => at(d, path)
        return (
          <>
            <DrillLinks links={LINKS} />
            {d.currencyNote && <p className="mis-muted" style={{ marginBottom: 12 }}>{d.currencyNote}</p>}

            <Section title="Cash & runway">
              <KpiGrid>
                <KpiTile label="Cash (book balance)" metric={m('cash.bookBalance')} format="currency" />
                <KpiTile label="Verified cash" metric={m('cash.verifiedCash')} format="currency" />
                <KpiTile label="Cash accounts" metric={m('cash.accountCount')} to="/accounts/cash-accounts" />
                <KpiTile label="Runway" metric={m('runway.months')} format="months" to="/accounts/reports/runway-sensitivity" />
                <KpiTile label="Projected runway" metric={m('runway.projectedMonths')} format="months" />
                <KpiTile label="Weighted pipeline (90d)" metric={m('pipeline.weighted90Day')} format="currency" />
              </KpiGrid>
            </Section>

            <div className="mis-two-col">
              <Section title="Cash flow (period)">
                <KpiGrid>
                  <KpiTile label="Inflow" metric={m('cash.netCashFlow.inflow')} format="currency" />
                  <KpiTile label="Outflow" metric={m('cash.netCashFlow.outflow')} format="currency" />
                  <KpiTile label="Net" metric={m('cash.netCashFlow.net')} format="currency" />
                </KpiGrid>
              </Section>
              <Section title="Burn">
                <KpiGrid>
                  <KpiTile label="Month to date" metric={m('burn.monthToDate')} format="currency" to="/accounts/reports/burn-discipline" />
                  <KpiTile label="Trailing 30 days" metric={m('burn.trailing30Day')} format="currency" />
                  <KpiTile label="Period" metric={m('burn.period')} format="currency" />
                  <KpiTile label="Entries" metric={m('burn.entryCount')} />
                </KpiGrid>
              </Section>
            </div>

            <Section title="Revenue & collections">
              <KpiGrid>
                <KpiTile label="Invoiced (pre-tax)" metric={m('revenue.invoicedTaxable')} format="currency" />
                <KpiTile label="Invoiced (with tax)" metric={m('revenue.invoicedWithTax')} format="currency" />
                <KpiTile label="GST (period)" metric={m('revenue.gstAmount')} format="currency" />
                <KpiTile label="Previous period" metric={m('revenue.previousPeriodTaxable')} format="currency" />
                <KpiTile label="Growth" metric={m('revenue.growthPercent')} format="signedPercent" />
                <KpiTile label="Collections received" metric={m('collections.received')} format="currency" />
                <KpiTile label="Collections cleared" metric={m('collections.cleared')} format="currency" />
                <KpiTile label="Collection / billing" metric={m('collections.collectionToBillingPercent')} format="percent" />
                <KpiTile label="Credit notes" metric={m('creditNotes')} format="currency" />
              </KpiGrid>
            </Section>

            <Section title="Invoices & GST — MTD / YTD" subtitle="MTD = calendar month to as-of · YTD = financial year to as-of">
              <KpiGrid>
                <KpiTile label="Invoices MTD" metric={m('revenue.mtd.invoiceCount')} />
                <KpiTile label="Invoiced MTD (pre-tax)" metric={m('revenue.mtd.invoicedTaxable')} format="currency" />
                <KpiTile label="GST amount MTD" metric={m('revenue.mtd.gstAmount')} format="currency" />
                <KpiTile label="Invoices YTD" metric={m('revenue.ytd.invoiceCount')} />
                <KpiTile label="Invoiced YTD (pre-tax)" metric={m('revenue.ytd.invoicedTaxable')} format="currency" />
                <KpiTile label="GST amount YTD" metric={m('revenue.ytd.gstAmount')} format="currency" />
              </KpiGrid>
            </Section>

            <div className="mis-two-col">
              <Section title="Receivables">
                <KpiGrid>
                  <KpiTile label="Outstanding" metric={m('receivables.outstanding')} format="currency" to="/management/reports/customer-collection" />
                  <KpiTile label="Overdue" metric={m('receivables.overdue')} format="currency" />
                  <KpiTile label="Overdue %" metric={m('receivables.overduePercent')} format="percent" />
                  <KpiTile label="DSO" metric={m('receivables.dsoDays')} format="days" />
                </KpiGrid>
              </Section>
              <Section title={`Budget — FY ${d.budget?.financialYear || ''}`}>
                <KpiGrid>
                  <KpiTile label="Budget status" metric={m('budget.status')} />
                  <KpiTile label="Monthly budget" metric={m('budget.monthlyBudget')} format="currency" />
                  <KpiTile label="MTD variance" metric={m('budget.varianceMonthToDatePercent')} format="signedPercent" to="/expenses/budget-vs-actual" />
                </KpiGrid>
              </Section>
            </div>

            <Section title="Cash & bank accounts">
              {isMasked(d.cash)
                ? <Unavailable note="Restricted — requires sensitive finance permission" />
                : (
                  <DataTable
                    rows={d.cash?.accounts || []}
                    emptyText="No active cash or bank accounts configured."
                    columns={[
                      { key: 'name', label: 'Account' },
                      { key: 'type', label: 'Type' },
                      { key: 'currency', label: 'Currency' },
                      { key: 'bookBalance', label: 'Book balance', format: 'currency' }
                    ]}
                  />
                )}
            </Section>
          </>
        )
      }}
    </ManagementReportShell>
  )
}

export default FinancialHealthReport
